import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class EposNowOutletMappingReadOnly extends ModelObject<EposNowOutletMappingReadOnly> {

    /**
    * The Epos Now settings which this mapping belongs to.
    */
    public eposNowSettingsId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.eposNowSettingsId = Guid.parseOrEmpty(jsonSerializedObject.eposNowSettingsId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.eposNowSettingsId = this.eposNowSettingsId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "eposNowSettingsId", rule: (v: any) => !v.isEmpty() || "EposNowOutletMappingReadOnly.EposNowSettingsId cannot be empty"},
    ];
}


export class EposNowOutletMapping extends ModelObject<EposNowOutletMapping> {

    /**
    * Can either be device name or location name. On order import, order is mapped using device name first, followed by location name if not found. 
    * Not Nullable
    */
    public eposNowOrderLocationIdentfier: string = "";
    /**
    * The Brewman outlet id to use for orders for the given device or location name.
    */
    public brewmanOutletId: Guid = Guid.createEmpty();
    public readOnly: EposNowOutletMappingReadOnly = new EposNowOutletMappingReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.eposNowOrderLocationIdentfier = jsonSerializedObject.eposNowOrderLocationIdentfier;
          this.brewmanOutletId = Guid.parseOrEmpty(jsonSerializedObject.brewmanOutletId);
          this.readOnly = new EposNowOutletMappingReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.eposNowOrderLocationIdentfier = this.eposNowOrderLocationIdentfier;
        toRet.brewmanOutletId = this.brewmanOutletId.toString();
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "brewmanOutletId", rule: (v: any) => !v.isEmpty() || "EposNowOutletMapping.BrewmanOutletId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class EposNowSettingsReadOnly extends ModelObject<EposNowSettingsReadOnly> {

    public apiKeyLength: number = 0;
    /**
    * The EposNow Company Identifier to which we are connected. This comes in a field called CompanyGuid from the EposNow api, so it is probably a guid, but it is of type string so we will treat it as a string.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public eposNowCompanyId: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.apiKeyLength = jsonSerializedObject.apiKeyLength;
          this.eposNowCompanyId = jsonSerializedObject.eposNowCompanyId;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.apiKeyLength = this.apiKeyLength;
        toRet.eposNowCompanyId = this.eposNowCompanyId;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "apiKeyLength", rule: (v: any) => v == null || v >= -2147483648 || "ApiKeyLength is not greater than minimum allowed value: -2147483648"},
            {field: "apiKeyLength", rule: (v: any) => v == null || v <= 2147483647 || "ApiKeyLength is above maximum allowed value: 2147483647"},
    ];
}


export class EposNowSettings extends ModelObject<EposNowSettings> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    private _defaultServiceChargeStockItemId: Guid | null = null;
    /**
    * Default Stock Item Id to be used for service charges on imported orders. 
    */
    public get defaultServiceChargeStockItemId(): Guid | null { return this._defaultServiceChargeStockItemId }
    public set defaultServiceChargeStockItemId(value: Guid | null) {
      this._defaultServiceChargeStockItemId = value === undefined ? null : value;
    }
    private _outletId: Guid | null = null;
    /**
    * Outlet Id. Required in order to perform import. Should be Null if Epos Now is not in use for the tenant or if tenant has outlet mapping.
    */
    public get outletId(): Guid | null { return this._outletId }
    public set outletId(value: Guid | null) {
      this._outletId = value === undefined ? null : value;
    }
    private _ignoreBeforeDate: LocalDate | null = null;
    /**
    * If provided then orders created on Epos Now from before this date will be ignored.
    */
    public get ignoreBeforeDate(): LocalDate | null { return this._ignoreBeforeDate }
    public set ignoreBeforeDate(value: LocalDate | null) {
      this._ignoreBeforeDate = value === undefined ? null : value;
    }
    private _priceListId: Guid | null = null;
    /**
    * PriceList Id. If provided then Products on EposNow will be updated with the List Price in BrewMan.
    */
    public get priceListId(): Guid | null { return this._priceListId }
    public set priceListId(value: Guid | null) {
      this._priceListId = value === undefined ? null : value;
    }
    /**
    * Whether the current integration should run continuously in the background.
    */
    public runInBackground: boolean = false;
    public readOnly: EposNowSettingsReadOnly = new EposNowSettingsReadOnly();
    public outletMappings: (EposNowOutletMapping)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.defaultServiceChargeStockItemId = jsonSerializedObject.defaultServiceChargeStockItemId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.defaultServiceChargeStockItemId);
          this.outletId = jsonSerializedObject.outletId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.outletId);
          this.ignoreBeforeDate = jsonSerializedObject.ignoreBeforeDate == null ? null : LocalDate.parse(jsonSerializedObject.ignoreBeforeDate);
          this.priceListId = jsonSerializedObject.priceListId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.priceListId);
          this.runInBackground = jsonSerializedObject.runInBackground;
          this.readOnly = new EposNowSettingsReadOnly(jsonSerializedObject.readOnly);
          this.outletMappings = jsonSerializedObject.outletMappings.map((v: any) => new EposNowOutletMapping(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.defaultServiceChargeStockItemId = this.defaultServiceChargeStockItemId == null ? null : this.defaultServiceChargeStockItemId.toString();
        toRet.outletId = this.outletId == null ? null : this.outletId.toString();
        toRet.ignoreBeforeDate = this.ignoreBeforeDate == null ? null : this.ignoreBeforeDate.toString();
        toRet.priceListId = this.priceListId == null ? null : this.priceListId.toString();
        toRet.runInBackground = this.runInBackground;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.outletMappings = this.outletMappings.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "EposNowSettings.Id cannot be empty"},
            {field: "defaultServiceChargeStockItemId", rule: (v: any) => v == null || !v.isEmpty() || "EposNowSettings.DefaultServiceChargeStockItemId cannot be empty"},
            {field: "outletId", rule: (v: any) => v == null || !v.isEmpty() || "EposNowSettings.OutletId cannot be empty"},
            {field: "priceListId", rule: (v: any) => v == null || !v.isEmpty() || "EposNowSettings.PriceListId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
            {field: "outletMappings", rule: (v: any) => !!v || "outletMappings is required"},
            {field: "outletMappings", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as EposNowOutletMapping[]).reduce((acc: Array<string>, curr: EposNowOutletMapping) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class TenantSettingsId extends ModelObject<TenantSettingsId> {

    public tenantId: Guid = Guid.createEmpty();
    public eposNowSettingsId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.tenantId = Guid.parseOrEmpty(jsonSerializedObject.tenantId);
          this.eposNowSettingsId = Guid.parseOrEmpty(jsonSerializedObject.eposNowSettingsId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.tenantId = this.tenantId.toString();
        toRet.eposNowSettingsId = this.eposNowSettingsId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "tenantId", rule: (v: any) => !v.isEmpty() || "TenantSettingsId.TenantId cannot be empty"},
            {field: "eposNowSettingsId", rule: (v: any) => !v.isEmpty() || "TenantSettingsId.EposNowSettingsId cannot be empty"},
    ];
}


export class EposNowSettingsService extends ServiceBase {

  public async getEposNowSettings(id: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<EposNowSettings> {
    const req = {
    tenantId:api.tenantId.toString(),
    id:id.toString(),
    };
    const extractor = (response:any) => new EposNowSettings(response.eposNowSettings);;
    return this.makeJsonRequest("eposNowSettings/v2/getEposNowSettings", req, false, extractor, opts);
  };

  public async getAllEposNowSettings(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(EposNowSettings)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.eposNowSettings.map((v: any) => new EposNowSettings(v));;
    return this.makeJsonRequest("eposNowSettings/v2/getAllEposNowSettings", req, false, extractor, opts);
  };

  public async saveEposNowSettings(eposNowSettingsId: Guid, eposNowSettings: EposNowSettings | null, apiKey: string | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    eposNowSettingsId:eposNowSettingsId.toString(),
    eposNowSettings:eposNowSettings == null ? null : eposNowSettings?.toJsonSerializedObject() ?? null,
    apiKey:apiKey == null ? null : apiKey,
    };
    const extractor = null;
    return this.makeJsonRequest("eposNowSettings/v2/saveEposNowSettings", req, true, extractor, opts);
  };

  public async hardDeleteEposNowSettings(eposNowSettingsId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    eposNowSettingsId:eposNowSettingsId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("eposNowSettings/v2/hardDeleteEposNowSettings", req, true, extractor, opts);
  };
}

export const eposNowSettingsService = new EposNowSettingsService();